import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "../../api";
import { Download, Upload, Loader2, CheckCircle2, AlertCircle, FileSpreadsheet, ImagePlus, X, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { BACKEND_URL, ADMIN_TOKEN_KEY } from "../../api";

const TITLES = {
  lessons: "Weekly Content (Stories · Questions · Activity · Reflection)",
  stories: "Stories",
  questions: "Questions",
  activities: "Activities",
  reflections: "Reflection Questions",
};

export default function AdminContent() {
  const { kind } = useParams();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [filterTheme, setFilterTheme] = useState("");
  const [filterAge, setFilterAge] = useState("");

  const load = async () => {
    const r = await api.get(`/admin/content/${kind}`);
    setItems(r.data);
  };

  useEffect(() => { load(); setUploadResult(null); }, [kind]);

  const downloadTemplate = async () => {
    const tk = localStorage.getItem(ADMIN_TOKEN_KEY);
    const url = `${BACKEND_URL}/api/admin/content/${kind}/template?t=${Date.now()}`;
    try {
      const res = await fetch(url, { headers: { Authorization: `Bearer ${tk}` } });
      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        toast.error(`Could not download template (${res.status}): ${txt.slice(0, 80)}`);
        return;
      }
      const blob = await res.blob();
      const filename = `${kind}_template.xlsx`;

      // Option 1: Native "Save As" dialog (Chrome/Edge. user picks location)
      if (window.showSaveFilePicker) {
        try {
          const handle = await window.showSaveFilePicker({
            suggestedName: filename,
            types: [{
              description: "Excel Spreadsheet",
              accept: { "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"] },
            }],
          });
          const writable = await handle.createWritable();
          await writable.write(blob);
          await writable.close();
          toast.success("Template saved");
          return;
        } catch (e) {
          if (e?.name === "AbortError") {
            // User cancelled. no toast needed
            return;
          }
          // Fall through to the legacy download path
        }
      }

      // Option 2: Legacy <a download> auto-download
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        document.body.removeChild(a);
      }, 200);

      // Option 3: Also open in a new tab as a fallback. user can right-click → Save As
      setTimeout(() => {
        window.open(blobUrl, "_blank");
        setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
      }, 400);

      toast.success("Template ready. check Downloads, or use the new tab to save");
    } catch (e) {
      toast.error("Network error downloading template");
    }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post(`/admin/content/${kind}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploadResult(r.data);
      if (r.data.errors?.length) toast.error(`${r.data.errors.length} row(s) need fixing`);
      else toast.success(`${r.data.validated_rows} rows validated`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); e.target.value = ""; }
  };

  const publish = async () => {
    if (!uploadResult || uploadResult.errors?.length) return;
    setBusy(true);
    try {
      const r = await api.post(`/admin/uploads/${uploadResult.upload_id}/publish`);
      toast.success(`Published ${r.data.published} items`);
      setUploadResult(null);
      load();
    } catch { toast.error("Publish failed"); }
    finally { setBusy(false); }
  };

  const filtered = items.filter((it) =>
    (!filterTheme || it.theme_id === filterTheme) &&
    (!filterAge || it.age_group === filterAge)
  );

  const onStoryImageSaved = (storyId, image_url) => {
    setItems((prev) => prev.map((it) => it.id === storyId ? { ...it, image_url } : it));
  };

  const deleteLesson = async (story) => {
    const confirm = window.prompt(
      `This will permanently delete the lesson:\n\n"${story.title}"\n(theme + week + age group ${story.age_group})\n\nincluding its questions, activity and reflection questions.\n\nType DELETE to confirm.`
    );
    if (confirm !== "DELETE") return;
    try {
      const r = await api.delete(`/admin/content/lessons/${story.id}`);
      const d = r.data.deleted;
      toast.success(`Deleted: story + ${d.questions} Qs + ${d.activities} activity + ${d.reflections} reflection Qs`);
      setItems((prev) => prev.filter((it) => it.id !== story.id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    }
  };

  const deleteAllLessons = async () => {
    const step1 = window.confirm(
      "⚠️ DANGER: This will permanently delete EVERY lesson in the database " +
      "(all stories, questions, activities and reflection questions across ALL " +
      "themes, weeks and age groups).\n\nThis cannot be undone. Existing parent " +
      "sessions in progress will keep their cached content, but any new session " +
      "will fail until you re-upload lessons.\n\nContinue?"
    );
    if (!step1) return;
    const confirm = window.prompt(
      'Final confirmation. Type "DELETE ALL" (all caps, with the space) to wipe every lesson.'
    );
    if (confirm !== "DELETE ALL") {
      toast.info("Cancelled. nothing was deleted.");
      return;
    }
    try {
      const r = await api.delete("/admin/content/lessons");
      const d = r.data.deleted;
      toast.success(`Wiped: ${d.stories} stories, ${d.questions} Qs, ${d.activities} activities, ${d.reflections} reflection Qs`);
      setItems([]);
      setUploadResult(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    }
  };

  return (
    <div data-testid={`admin-content-${kind}`}>
      <h1 className="font-display text-4xl mb-2">{TITLES[kind] || "Content"}</h1>
      <p className="text-muted-foreground mb-8">Upload via Excel · Validate · Preview · Publish.</p>

      <div className="card-soft p-6 mb-6">
        <div className="flex flex-wrap items-center gap-3">
          <button onClick={downloadTemplate} className="btn-ghost" data-testid="admin-template-btn">
            <Download className="w-4 h-4"/> Download template
          </button>
          <label className="btn-primary cursor-pointer" data-testid="admin-upload-label">
            <Upload className="w-4 h-4"/> Upload .xlsx
            <input type="file" accept=".xlsx" hidden onChange={onFile} disabled={busy}
                   data-testid="admin-upload-input"/>
          </label>
          {busy && <Loader2 className="w-4 h-4 animate-spin"/>}
          {kind === "lessons" && items.length > 0 && (
            <button
              onClick={deleteAllLessons}
              className="ml-auto text-sm text-destructive hover:bg-destructive/10 px-3 py-1.5 rounded-full flex items-center gap-2 border border-destructive/30"
              data-testid="admin-delete-all-lessons-btn"
            >
              <Trash2 className="w-4 h-4" /> Delete all lessons
            </button>
          )}
        </div>
      </div>

      {uploadResult && (
        <div className="card-elevated p-6 mb-6" data-testid="admin-upload-result">
          <div className="flex items-center gap-2 mb-3">
            {uploadResult.errors?.length ? (
              <AlertCircle className="w-5 h-5 text-destructive"/>
            ) : (
              <CheckCircle2 className="w-5 h-5 text-[hsl(var(--neu-orange))]"/>
            )}
            <h3 className="font-display text-xl">
              {uploadResult.errors?.length ? "Validation issues" : "Ready to publish"}
            </h3>
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            {uploadResult.validated_rows} validated · {uploadResult.errors?.length || 0} error(s)
          </p>
          {uploadResult.errors?.length > 0 && (
            <div className="space-y-1 text-sm mb-4">
              {uploadResult.errors.map((er, i) => (
                <div key={i} className="text-destructive">Row {er.row}: {er.error}</div>
              ))}
            </div>
          )}
          {uploadResult.rows?.length > 0 && (
            <details className="mb-4">
              <summary className="text-sm cursor-pointer text-muted-foreground">Preview rows</summary>
              <div className="mt-2 max-h-72 overflow-auto text-xs">
                <pre className="whitespace-pre-wrap">{JSON.stringify(uploadResult.rows.slice(0, 5), null, 2)}</pre>
              </div>
            </details>
          )}
          {!uploadResult.errors?.length && (
            <button onClick={publish} disabled={busy} className="btn-primary"
                    data-testid="admin-publish-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : "Publish"}
            </button>
          )}
        </div>
      )}

      <div className="card-soft overflow-hidden">
        <div className="p-4 border-b flex items-center gap-2">
          <FileSpreadsheet className="w-4 h-4 text-muted-foreground"/>
          <h3 className="font-medium">{filtered.length} published items</h3>
          <select className="ml-auto field py-1 px-3 text-sm w-auto"
                  value={filterAge} onChange={(e) => setFilterAge(e.target.value)}>
            <option value="">All ages</option>
            <option value="4-8">4-8</option>
            <option value="9-13">9-13</option>
          </select>
        </div>
        <div className="divide-y max-h-[60vh] overflow-y-auto">
          {filtered.slice(0, 200).map((it, i) => (
            <div key={it.id || i} className="p-4 text-sm relative">
              {kind === "lessons" && (
                <button
                  onClick={() => deleteLesson(it)}
                  className="absolute top-3 right-3 p-1.5 rounded-full text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                  title="Delete this lesson"
                  data-testid={`lesson-delete-btn-${it.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
              <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                <span className="chip">Age {it.age_group}</span>
                <span className="chip">Week id: {it.week_id?.slice(0, 8)}…</span>
                <span className="chip">{it.status}</span>
              </div>
              <div className="font-medium">
                {it.title || it.name || it.question_text || it.activity_name}
              </div>
              {(it.content_md || it.instructions_md) && (
                <div className="text-muted-foreground line-clamp-2 mt-1">
                  {(it.content_md || it.instructions_md).slice(0, 200)}…
                </div>
              )}
              {kind === "lessons" && it.week_objective && (
                <div className="mt-3 text-xs italic text-muted-foreground">
                  <b className="text-foreground not-italic">Week objective:</b> {it.week_objective}
                </div>
              )}
              {kind === "lessons" && it.questions?.length > 0 && (
                <div className="mt-3">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Guided questions</div>
                  <ol className="mt-1 pl-5 space-y-1 list-decimal text-xs text-muted-foreground"
                      data-testid={`lesson-questions-${it.id}`}>
                    {it.questions.map((q) => (
                      <li key={q.id}>
                        <span className="text-foreground">{q.question_text}</span>
                        {q.facilitation_hint && (
                          <span className="block italic opacity-70">↳ {q.facilitation_hint}</span>
                        )}
                      </li>
                    ))}
                  </ol>
                </div>
              )}
              {kind === "lessons" && it.activity && (
                <div className="mt-3">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Family activity · {it.activity.estimated_minutes || 10} min</div>
                  <div className="text-sm text-foreground mt-1"><b>{it.activity.name}</b></div>
                  {it.activity.objective && (
                    <div className="text-xs text-muted-foreground mt-1">
                      <b className="text-foreground">Objective:</b> {it.activity.objective}
                    </div>
                  )}
                  {it.activity.things_needed && (
                    <div className="text-xs text-muted-foreground mt-1">
                      <b className="text-foreground">Things needed:</b> <span className="whitespace-pre-line">{it.activity.things_needed}</span>
                    </div>
                  )}
                  <div className="text-xs text-muted-foreground line-clamp-3 mt-1">
                    {it.activity.instructions_md}
                  </div>
                </div>
              )}
              {kind === "lessons" && it.reflection_questions?.length > 0 && (
                <div className="mt-3">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Parent reflection</div>
                  <ol className="mt-1 pl-5 space-y-0.5 list-decimal text-xs text-muted-foreground">
                    {it.reflection_questions.map((r) => (
                      <li key={r.id}><span className="text-foreground">{r.question_text}</span></li>
                    ))}
                  </ol>
                </div>
              )}
              {(kind === "stories" || kind === "lessons") && (
                <StoryImageEditor story={it} onSaved={onStoryImageSaved} />
              )}
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="p-6 text-center text-muted-foreground text-sm">
              No content yet. Upload to start.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function resolveImg(url) {
  if (!url) return null;
  return url.startsWith("http") ? url : `${BACKEND_URL}${url}`;
}

function StoryImageEditor({ story, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [urlInput, setUrlInput] = useState(story.image_url || "");
  const [busy, setBusy] = useState(false);

  const save = async (image_url) => {
    setBusy(true);
    try {
      await api.patch(`/admin/content/stories/${story.id}`, { image_url });
      toast.success(image_url ? "Image attached" : "Image removed");
      onSaved(story.id, image_url || null);
      setEditing(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save image");
    } finally { setBusy(false); }
  };

  const onPick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error("Max 5 MB"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/admin/upload/image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await save(r.data.image_url);
      setUrlInput(r.data.image_url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
      setBusy(false);
    }
  };

  const current = resolveImg(story.image_url);

  return (
    <div className="mt-3 flex flex-wrap items-center gap-3" data-testid={`story-image-editor-${story.id}`}>
      {current ? (
        <img src={current} alt="" className="w-20 h-20 rounded-lg border bg-[#FFFBF5]"
             style={{ objectFit: "contain" }}
             data-testid={`story-image-thumb-${story.id}`} />
      ) : (
        <div className="w-20 h-20 rounded-lg border-2 border-dashed border-muted-foreground/30 flex items-center justify-center text-muted-foreground">
          <ImagePlus className="w-5 h-5" />
        </div>
      )}

      {!editing ? (
        <>
          <button
            onClick={() => setEditing(true)}
            className="btn-ghost text-xs"
            data-testid={`story-image-edit-btn-${story.id}`}
            disabled={busy}
          >
            {story.image_url ? "Change image" : "Add image"}
          </button>
          {story.image_url && (
            <button
              onClick={() => save("")}
              className="text-xs text-destructive hover:underline"
              data-testid={`story-image-remove-btn-${story.id}`}
              disabled={busy}
            >
              Remove
            </button>
          )}
        </>
      ) : (
        <div className="flex flex-wrap items-center gap-2 flex-1 min-w-[280px]">
          <label className="btn-ghost text-xs cursor-pointer">
            <Upload className="w-3.5 h-3.5" /> Upload file
            <input type="file" accept="image/png,image/jpeg,image/webp,image/gif"
                   hidden onChange={onPick} disabled={busy}
                   data-testid={`story-image-file-input-${story.id}`} />
          </label>
          <span className="text-xs text-muted-foreground">or</span>
          <input
            type="url"
            placeholder="Paste image URL (https://…)"
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            className="field flex-1 min-w-[180px] text-xs py-1.5"
            data-testid={`story-image-url-input-${story.id}`}
          />
          <button
            onClick={() => save(urlInput.trim())}
            className="btn-primary text-xs py-1.5"
            disabled={busy}
            data-testid={`story-image-save-btn-${story.id}`}
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Save"}
          </button>
          <button
            onClick={() => { setEditing(false); setUrlInput(story.image_url || ""); }}
            className="btn-ghost text-xs py-1.5"
            disabled={busy}
            data-testid={`story-image-cancel-btn-${story.id}`}
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}
